import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { supabase } from '@/lib/supabase';
import FormEncontro from './FormEncontro';
import { mensagemErro } from '../utils';
import type { Encontro } from '../types';

export interface PassoDatasHandle {
  validar: () => boolean;
}

interface PassoDatasProps {
  eventoId: number | null;
  cidade: string;
  aoPular: () => void;
}

function formatarEncontro(inicioEm: string): string {
  const d = new Date(inicioEm);
  if (Number.isNaN(d.getTime())) return '';
  const data = d.toLocaleDateString('pt-BR');
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${data} · ${hora}`;
}

function resumoEncontro(e: Encontro): string {
  const partes: string[] = [];
  partes.push(e.local_nome || 'No local do evento');
  partes.push(e.para_quem || 'Para todos');
  partes.push(e.obrigatorio ? 'obrigatória' : 'facultativa');
  return partes.join(' · ');
}

function PassoDatas(
  { eventoId, cidade, aoPular }: PassoDatasProps,
  ref: React.Ref<PassoDatasHandle>,
) {
  const [encontros, setEncontros] = useState<Encontro[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [painelAberto, setPainelAberto] = useState(false);
  const [editando, setEditando] = useState<Encontro | null>(null);
  const [confirmarApagar, setConfirmarApagar] = useState<number | null>(null);
  const [apagando, setApagando] = useState(false);

  useImperativeHandle(ref, () => ({
    validar: () => true,
  }));

  const carregar = useCallback(async () => {
    if (!eventoId) {
      setCarregando(false);
      return;
    }
    setCarregando(true);
    setErro(null);
    const { data, error } = await supabase
      .from('vw_encontros_evento')
      .select('*')
      .eq('evento_id', eventoId)
      .order('inicio_em');
    if (error) {
      setErro(mensagemErro(error, 'Não foi possível carregar as datas.'));
    } else {
      setEncontros((data as Encontro[]) || []);
    }
    setCarregando(false);
  }, [eventoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  function abrirNovo() {
    setEditando(null);
    setPainelAberto(true);
  }

  function abrirEditar(e: Encontro) {
    setEditando(e);
    setPainelAberto(true);
  }

  function fecharPainel() {
    setPainelAberto(false);
    setEditando(null);
  }

  function aoSalvar() {
    fecharPainel();
    carregar();
  }

  async function apagar(id: number) {
    setApagando(true);
    const { error } = await supabase.rpc('apagar_encontro', { p_encontro_id: id });
    setApagando(false);
    if (error) {
      setErro(mensagemErro(error, 'Não foi possível apagar. Tente novamente.'));
      return;
    }
    setConfirmarApagar(null);
    carregar();
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-normal text-foreground-950">Datas do evento</h2>
        <p className="mt-1 text-sm text-secondary-500">
          Um acampamento costuma ter formações, vivência e missa de envio. Cadastre essas datas
          aqui: elas aparecem para quem se inscreveu, e entram na agenda da cidade — para ninguém
          marcar outra coisa em cima.
        </p>
      </div>

      {erro && (
        <div className="flex items-start justify-between gap-3 rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          <span>{erro}</span>
          <button
            type="button"
            onClick={() => carregar()}
            className="cursor-pointer whitespace-nowrap font-medium underline"
          >
            Tentar de novo
          </button>
        </div>
      )}

      {carregando ? (
        <div className="space-y-3">
          <div className="h-16 w-full animate-pulse rounded-lg bg-secondary-100" />
          <div className="h-16 w-full animate-pulse rounded-lg bg-secondary-100" />
        </div>
      ) : (
        <>
          {encontros.length === 0 ? (
            <div className="rounded-lg border border-secondary-200 bg-white px-5 py-8 text-center">
              <p className="text-sm text-secondary-500">
                Nenhuma data cadastrada ainda. Pode pular esta etapa ou adicionar a primeira data.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
              {encontros.map((e, i) => (
                <div key={e.id} className={i > 0 ? 'border-t border-secondary-100' : ''}>
                  {confirmarApagar === e.id ? (
                    <div className="px-4 py-3">
                      <p className="text-sm text-foreground-900">
                        Apagar <strong>{e.nome}</strong>? Quem já se inscreveu deixa de ver esta
                        data.
                      </p>
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => apagar(e.id)}
                          disabled={apagando}
                          className="cursor-pointer whitespace-nowrap rounded-md bg-danger-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-danger-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {apagando ? 'Apagando…' : 'Apagar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmarApagar(null)}
                          disabled={apagando}
                          className="cursor-pointer whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-100"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[15px] font-medium text-foreground-950">
                          {formatarEncontro(e.inicio_em)} · {e.nome}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-secondary-500">
                          {resumoEncontro(e)}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => abrirEditar(e)}
                          aria-label="Editar data"
                          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100 hover:text-foreground-800"
                        >
                          <i className="ri-edit-line text-lg" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmarApagar(e.id)}
                          aria-label="Apagar data"
                          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100 hover:text-danger-600"
                        >
                          <i className="ri-close-line text-lg" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={abrirNovo}
            className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border-2 border-dashed border-secondary-300 bg-white px-4 py-4 text-[15px] font-medium text-primary-600 transition-colors hover:border-primary-400 hover:bg-background-100"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-add-line" />
            </span>
            Adicionar data
          </button>

          <div className="flex justify-center">
            <button
              type="button"
              onClick={aoPular}
              className="cursor-pointer whitespace-nowrap text-[15px] font-medium text-secondary-600 transition-colors hover:text-foreground-800"
            >
              Pular por enquanto
            </button>
          </div>
        </>
      )}

      {painelAberto && eventoId && (
        <FormEncontro
          eventoId={eventoId}
          cidade={cidade}
          encontro={editando}
          aoFechar={fecharPainel}
          aoSalvar={aoSalvar}
        />
      )}
    </div>
  );
}

export default forwardRef(PassoDatas);